'use client'

import { memo } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { Camera, Gift, Heart, LoaderCircle, Medal, PackageX, Percent, ShoppingCart, Star } from 'lucide-react'
import {
  formatImageURL,
  formatPrice,
  getDisplayDiscounts,
  getEffectivePrice,
  getStrikethroughPrice,
  getSalePercent,
  getOtherVariantSaleType,
  getBuyXGetYTagText,
  getLoyaltyProductDiscount,
  isDiscountBetter,
  isCustomerAuthActive,
} from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { useCart, useImageCarousel, useProductCard, useWishlist } from '@/lib/commerce/hooks'
import VariantSelectionModal from './VariantSelectionModal'
import NoImage from '@/components/blocks/NoImage'
import ColorSwatches from '@/components/blocks/ColorSwatches'

function ProductCard({ product, vendor, showVariantName = false }: { product: any, vendor: any, showVariantName?: boolean }) {
  const { data: session } = useSession()
  const pathname = usePathname()
  const { isInWishlist, toggle: toggleWishlistHook, loading: wishlistLoading } = useWishlist()
  const { isProductAvailable, availableVariant, loading, adding, showVariantModal, setShowVariantModal, handleAddToCart, handleVariantAddToCart, colors, activeColor, selectColor, cardProduct } = useProductCard(product, vendor, showVariantName)
  const { hoveredImageIndex, setHoveredImageIndex, allImages, remainingCount, shouldLoadImage, hasImages, handleTouchStart, handleTouchEnd, onMouseEnter, onMouseLeave } = useImageCarousel(cardProduct)

  const localIsInWishlist = isInWishlist(product.id)
  const toggleWishlist = () => toggleWishlistHook({ productId: product.id, variantId: product.variant?.id })

  const { loyaltySummary, loyaltyCategoryParents } = useCart()
  const discounts = getDisplayDiscounts(product, !!session?.user?.id, availableVariant?.id)
  const effectivePrice = getEffectivePrice(availableVariant, discounts)
  const strikethroughPrice = getStrikethroughPrice(availableVariant, discounts)
  const variantHasPriceSale = !!(availableVariant?.priceSale && availableVariant?.price && availableVariant.priceSale < availableVariant.price)
  const showsDiscountBadge = discounts?.length > 0 && isDiscountBetter(availableVariant, discounts)
  const loyaltyDeal = getLoyaltyProductDiscount({ product, variant: availableVariant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: showsDiscountBadge })
  const displayEffective = loyaltyDeal ? Math.max(0, (effectivePrice ?? availableVariant?.price ?? 0) - loyaltyDeal.unitSaving) : effectivePrice
  const displayStrike = loyaltyDeal ? (availableVariant?.price ?? 0) : strikethroughPrice
  const otherVariantSale = (!variantHasPriceSale && !(showsDiscountBadge && isProductAvailable))
    ? getOtherVariantSaleType(product, availableVariant?.id, !!session?.user?.id)
    : null

  const productHref = navigateUrl(`/product/${product.customId || product.id}`, pathname)
  const variantName = product?.variant?.name

  return (
    <div className="group block">
      <div
        className="relative aspect-4/5 rounded-xl overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {!hasImages && <NoImage light />}
        {hasImages && allImages.map((img: string, index: number) => shouldLoadImage(index) && (
          <img
            key={index}
            src={formatImageURL(img)}
            alt={product?.name}
            loading="lazy"
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out will-change-transform ${hoveredImageIndex === index ? 'opacity-100' : 'opacity-0'} group-hover:scale-105`}
          />
        ))}
        {remainingCount > 0 && hoveredImageIndex === 4 && (
          <div className="absolute inset-0 bg-black/60 flex flex-col gap-1 items-center justify-center">
            <Camera className="h-7 w-7 text-white" />
            <span className="text-white font-semibold text-[14px]">+{remainingCount} зураг</span>
          </div>
        )}
        <a href={productHref} aria-label={product?.name} className="absolute inset-0 z-0" />
        <div className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.45) 100%)' }} />

        {!isProductAvailable ? (
          <span
            className="absolute top-3 left-3 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] z-10 backdrop-blur-md"
            style={{ background: 'rgba(0,0,0,0.5)', color: 'white', border: '1px solid rgba(255,255,255,0.16)' }}
          >
            Дууссан
          </span>
        ) : showsDiscountBadge ? (
          <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-1">
            {discounts.map((discount: any, index: number) => (
              <span key={discount.id || index} className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] tabular-nums" style={{ background: 'white', color: '#0a0a0c' }}>
                {discount.type === 'productDiscount' && discount.valueType === 'percent' && discount.valuePercent ? (
                  discount.valuePercent >= 100 ? 'Үнэгүй' : `-${discount.valuePercent}% off`
                ) : discount.type === 'productDiscount' && discount.valueType === 'amount' && discount.valueAmount ? (
                  `-${formatPrice(discount.valueAmount)}`
                ) : discount.type === 'buyXgetY' ? (
                  getBuyXGetYTagText(discount)
                ) : null}
              </span>
            ))}
            {!discounts.some((d: any) => d.type === 'productDiscount') && variantHasPriceSale && !loyaltyDeal?.saleOffset && (
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] tabular-nums" style={{ background: 'white', color: '#0a0a0c' }}>
                -{getSalePercent(availableVariant)}% off
              </span>
            )}
            {loyaltyDeal && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] tabular-nums bg-violet-600 text-white">
                <Medal className="h-3 w-3" />
                -{loyaltyDeal.percent}%
              </span>
            )}
          </div>
        ) : (variantHasPriceSale || loyaltyDeal) ? (
          <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-1">
            {variantHasPriceSale && !loyaltyDeal?.saleOffset && (
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] tabular-nums" style={{ background: 'white', color: '#0a0a0c' }}>
                -{getSalePercent(availableVariant)}% off
              </span>
            )}
            {loyaltyDeal && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] tabular-nums bg-violet-600 text-white">
                <Medal className="h-3 w-3" />
                -{loyaltyDeal.percent}%
              </span>
            )}
          </div>
        ) : otherVariantSale ? (
          <span className="absolute top-3 left-3 z-10 inline-flex items-center justify-center w-7 h-7 rounded-full backdrop-blur-md" style={{ background: 'rgba(0,0,0,0.4)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
            {otherVariantSale === 'discount' ? <Gift className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Percent className="w-3.5 h-3.5" strokeWidth={2.5} />}
          </span>
        ) : null}

        {isCustomerAuthActive(vendor) && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); toggleWishlist() }}
            aria-label="Хадгалах"
            className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-full backdrop-blur-md z-10 transition-colors cursor-pointer hover:bg-black/60"
            style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.2)', color: 'white' }}
          >
            {wishlistLoading ? (
              <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Heart className="h-3.5 w-3.5" fill={localIsInWishlist ? 'currentColor' : 'none'} />
            )}
          </button>
        )}

        {vendor?.canPlaceOrder !== false && (
          <div className={`absolute left-3 right-3 z-20 hidden sm:[@media(hover:hover)]:block opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 ease-out ${allImages.length > 1 ? 'bottom-7' : 'bottom-3'}`}>
            {isProductAvailable ? (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); handleAddToCart() }}
                disabled={loading}
                className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-full text-[12.5px] font-medium tracking-[-0.005em] transition-colors duration-200 disabled:opacity-60"
                style={{ background: 'white', color: '#0a0a0c', boxShadow: '0 10px 24px -6px rgba(0,0,0,0.45)' }}
              >
                {loading ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <ShoppingCart className="h-3.5 w-3.5" strokeWidth={2} />}
                Сагсанд хийх
              </button>
            ) : (
              <button
                type="button"
                disabled
                className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-full text-[12.5px] font-medium tracking-[-0.005em] backdrop-blur-md"
                style={{ background: 'rgba(10,10,12,0.7)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.16)' }}
              >
                <PackageX className="h-3.5 w-3.5" strokeWidth={2} />
                Дууссан
              </button>
            )}
          </div>
        )}

        {vendor?.canPlaceOrder !== false && isProductAvailable && (
          <button type="button" onClick={(e) => { e.preventDefault(); handleAddToCart() }} disabled={adding} aria-label="Сагсанд хийх" className="sm:hidden absolute bottom-3 right-3 z-20 w-9 h-9 inline-flex items-center justify-center rounded-full disabled:opacity-60" style={{ background: 'white', color: '#0a0a0c', boxShadow: '0 10px 24px -6px rgba(0,0,0,0.45)' }}>
            {adding ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" strokeWidth={2} />}
          </button>
        )}

        {allImages.length > 1 && (
          <div className="absolute bottom-1.5 left-4 right-4 z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden sm:flex gap-1">
            {allImages.map((_: string, index: number) => (
              <div key={index} onMouseEnter={() => setHoveredImageIndex(index)} className="flex-1 h-3 flex items-end pointer-events-auto">
                <div className={`w-full h-0.5 rounded-full transition-all ${hoveredImageIndex === index ? 'bg-white' : 'bg-white/40'}`} />
              </div>
            ))}
          </div>
        )}
        {allImages.length > 1 && (
          <div className="absolute bottom-2 left-0 right-0 z-10 sm:hidden pointer-events-none flex justify-center gap-1.5">
            {allImages.map((_: string, index: number) => (
              <div key={index} className={`w-1.5 h-1.5 rounded-full transition-all ${hoveredImageIndex === index ? 'bg-white' : 'bg-white/40'}`} />
            ))}
          </div>
        )}
      </div>

      {colors.length > 0 && (
        <div className="mt-2 h-6 flex items-center justify-between gap-3">
          <ColorSwatches colors={colors} active={activeColor} onSelect={selectColor} tone="dark" max={colors.length > 4 ? 3 : 4} className="sm:hidden flex-nowrap shrink-0 -ml-0.75" />
          <ColorSwatches colors={colors} active={activeColor} onSelect={selectColor} tone="dark" className="hidden sm:flex flex-nowrap shrink-0 -ml-0.75" />
          <div className="hidden sm:block min-w-0 text-[10px] font-semibold uppercase tracking-[0.22em] truncate" style={{ color: '#a8a8b3' }}>{product?.brand?.name || ''}</div>
        </div>
      )}

      <a href={productHref} className={`block ${colors.length > 0 ? '' : 'mt-4'} pb-3`} style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        {colors.length === 0 && (
          <div className="h-4 text-[10px] font-semibold uppercase tracking-[0.22em] truncate" style={{ color: '#a8a8b3' }}>
            {product?.brand?.name || ''}
          </div>
        )}
        <div className="mt-1 text-[14px] font-medium leading-snug line-clamp-1" style={{ color: 'rgba(255,255,255,0.95)', fontFamily: 'Gilroy, Inter, sans-serif' }}>
          {product?.name}
        </div>
        {showVariantName && variantName && (
          <div className="text-[12px] mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>{variantName}</div>
        )}
        <div className="flex items-center justify-between mt-1.5 gap-2">
          {isProductAvailable ? (
            availableVariant?.price && availableVariant.price > 0 ? (
              displayEffective !== null ? (
                displayEffective === 0 ? (
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="text-[14px] font-semibold tabular-nums" style={{ color: '#86efac' }}>Үнэгүй</span>
                    <span className="text-[11px] tabular-nums line-through truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>{formatPrice(displayStrike)}</span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="text-[14px] font-semibold tabular-nums text-white">{formatPrice(displayEffective)}</span>
                    <span className="text-[11px] tabular-nums line-through truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>{formatPrice(displayStrike)}</span>
                  </div>
                )
              ) : (
                <span className="text-[14px] font-semibold tabular-nums text-white">{formatPrice(availableVariant.price)}</span>
              )
            ) : <span />
          ) : (
            <span className="text-[13px]" style={{ color: 'rgba(255,255,255,0.5)' }}>Дууссан</span>
          )}
          {vendor?.canReview && (product.averageRating || product.reviewCount) ? (
            <span className="inline-flex items-center gap-1 text-[11px] tabular-nums shrink-0" style={{ color: 'rgba(255,255,255,0.6)' }}>
              <Star className="h-3 w-3 fill-current" style={{ color: '#fcd34d' }} />
              <span>{product.averageRating || 0}</span>
              {product.reviewCount > 0 && <span style={{ color: 'rgba(255,255,255,0.4)' }}>({product.reviewCount})</span>}
            </span>
          ) : null}
        </div>
      </a>

      <VariantSelectionModal isOpen={showVariantModal} onClose={() => setShowVariantModal(false)} product={cardProduct} vendor={vendor} onAddToCart={handleVariantAddToCart} />
    </div>
  )
}

// memo: appending a page of 20 otherwise re-renders every card already in the grid
export default memo(ProductCard)
